import { useState } from 'preact/hooks';
import { currentScreen, gameSetup } from '../../App';
import { sfx } from '../../lib/sfx';
import type { DurationTier } from '@mxltiverse/shared';
import { DURATION_TIERS } from '@mxltiverse/shared';

type Lang = 'es' | 'en' | 'pt';

const TIER_INFO: Record<DurationTier, { tagline: Record<Lang, string>; desc: Record<Lang, string> }> = {
  quick_play: {
    tagline: { es: 'Una mordida rapida de accion', en: 'A quick bite of action', pt: 'Uma mordida rápida de ação' },
    desc: {
      es: 'Una escena intensa. Perfecta para un descanso.',
      en: 'One intense scene. Perfect for a break.',
      pt: 'Uma cena intensa. Perfeita para uma pausa.',
    },
  },
  aventura: {
    tagline: { es: 'Un episodio completo', en: 'A full episode', pt: 'Um episódio completo' },
    desc: {
      es: 'Inicio, nudo y desenlace en una sesion.',
      en: 'Beginning, middle and end in one session.',
      pt: 'Início, meio e fim em uma sessão.',
    },
  },
  historia: {
    tagline: { es: 'Una saga completa', en: 'A full saga', pt: 'Uma saga completa' },
    desc: {
      es: 'Arco narrativo profundo con multiples actos.',
      en: 'Deep narrative arc with multiple acts.',
      pt: 'Arco narrativo profundo com múltiplos atos.',
    },
  },
};

export function DurationSelect() {
  const [selected, setSelected] = useState<DurationTier | null>(null);
  const lang = (gameSetup.value.language ?? 'en') as Lang;

  const labels = {
    title: { es: 'DURACION DE LA PARTIDA', en: 'SESSION DURATION', pt: 'DURAÇÃO DA PARTIDA' },
    back: { es: 'Volver', en: 'Back', pt: 'Voltar' },
    next: { es: 'Crear personaje', en: 'Create character', pt: 'Criar personagem' },
    minutes: { es: 'min', en: 'min', pt: 'min' },
    turns: { es: 'turnos aprox.', en: 'turns approx.', pt: 'turnos aprox.' },
  };

  const tiers = Object.entries(DURATION_TIERS) as [DurationTier, typeof DURATION_TIERS[DurationTier]][];

  function handleConfirm() {
    if (!selected) return;
    sfx.success();
    gameSetup.value = { ...gameSetup.value, duration: selected };
    // TODO: navigate to game screen (M4)
    currentScreen.value = 'character';
  }

  return (
    <div class="center-screen">
      <div class="pixel-border container">
        <h2 class="pixel-title text-yellow mb-3 text-center" style={{ fontSize: '18px' }}>
          {labels.title[lang]}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {tiers.map(([id, cfg]) => {
            const info = TIER_INFO[id];
            const isSelected = selected === id;
            return (
              <div
                key={id}
                class="pixel-border"
                style={{
                  cursor: 'pointer',
                  borderColor: isSelected ? 'var(--accent-green)' : undefined,
                  background: isSelected ? 'var(--bg-panel-light)' : undefined,
                  transition: 'all 0.1s',
                }}
                onClick={() => { sfx.select(); setSelected(id); }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 class="text-yellow" style={{ fontSize: '22px' }}>{cfg.label}</h3>
                  <span class="text-dim" style={{ fontSize: '16px' }}>
                    {cfg.min_min}-{cfg.max_min} {labels.minutes[lang]}
                  </span>
                </div>
                <p class="text-green" style={{ fontSize: '16px' }}>{info.tagline[lang]}</p>
                <p class="text-dim" style={{ fontSize: '14px' }}>{info.desc[lang]}</p>
                <p class="text-dim" style={{ fontSize: '13px' }}>
                  ~{cfg.turn_budget} {labels.turns[lang]} | {cfg.act_count} actos
                </p>
              </div>
            );
          })}
        </div>

        <div class="mt-3" style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button class="pixel-btn" onClick={() => { sfx.click(); currentScreen.value = 'genre'; }}>
            {labels.back[lang]}
          </button>
          <button class="pixel-btn text-green" disabled={!selected} onClick={handleConfirm}>
            {labels.next[lang]}
          </button>
        </div>
      </div>
    </div>
  );
}
